import type { GradedAnswer } from "../fuxi/answer-marks.js";
import type { HanziAssembly } from "../fuxi/hanzi-assembly-table.js";
import { bilingual, bilingualText, simplifiedLanguage } from "./bilingual.js";
import { escapeHtml } from "../site-search/search-escaping.js";
import { fuxiAssemblyFrame, fuxiAssemblySum } from "./fuxi-assembly-markup.js";
import { ratings } from "../fuxi/rating.js";

/**
 * The square a 汉字 is put together in once it is answered, and the sums
 * under it.
 *
 * Drawn by the marked card in 复习 (`./fuxi-assembled-marked.ts`) and by a
 * 拼字 Exercise on its page (`src/site/lianxi-pinzi.ts`), and played by
 * `src/site/fuxi-assembled.ts` in both. Apart from the card so that the
 * Exercise's bundle draws it with nothing of 复习's panel.
 */

/** One term of a sum, with the mark it earned or the part it names. */
function term(properties: {
  readonly part: string;
  readonly slot?: number;
  readonly mark?: string;
  readonly partOf?: number;
}): string {
  const slot =
    properties.slot === undefined
      ? ""
      : ` data-cb-fuxi-slot="${String(properties.slot)}"`;
  const mark =
    properties.mark === undefined
      ? ""
      : ` data-cb-fuxi-mark="${escapeHtml(properties.mark)}"`;
  const partOf =
    properties.partOf === undefined
      ? ""
      : ` data-cb-fuxi-part-of="${String(properties.partOf)}"`;

  return `<span class="cb-fuxi-zuzi-term"${slot}${mark}${partOf}>${escapeHtml(properties.part)}</span>`;
}

/**
 * The reader's sum, marked, and the character's own where theirs was not it.
 *
 * @param assembly - The character's assembly.
 * @param chosen - The parts the reader chose, in order.
 * @param graded - How they came out.
 * @returns The sums, and the line a pass in another order adds.
 */
export function fuxiAssembledSums(
  assembly: HanziAssembly,
  chosen: readonly string[],
  graded: GradedAnswer,
): string {
  if (chosen.length === 0) {
    return `<p class="cb-fuxi-typed cb-fuxi-typed-empty">${bilingual(
      "You left this one blank.",
      { simplified: "这道没答。", traditional: "這道沒答。" },
    )}</p>\n${answer(assembly)}`;
  }

  const right = graded.rating === ratings.good;
  const yours = chosen.map((part, slot) =>
    term({
      part,
      slot,
      mark: graded.pieces[slot]?.mark ?? "wrong",
      ...(right && { partOf: slot }),
    }),
  );
  const another =
    graded.rating === ratings.hard
      ? `\n<p class="cb-fuxi-another-order">${bilingual(
          "Right parts, different order.",
          {
            simplified: "部件都对，顺序不同。",
            traditional: "部件都對，順序不同。",
          },
        )}</p>`
      : "";

  return `<p class="cb-fuxi-zuzi-sum cb-fuxi-zuzi-yours" lang="${simplifiedLanguage}" data-cb-fuxi-zuzi-yours>${fuxiAssemblySum(yours)}</p>${another}${right ? "" : `\n${answer(assembly)}`}`;
}

/** The parts the character is put together from, as a sum. */
function answer(assembly: HanziAssembly): string {
  const named = bilingual("Answer", {
    simplified: "答案",
    traditional: "答案",
  });
  const terms = assembly.parts.map((part, at) => term({ part, partOf: at }));

  return `<p class="cb-fuxi-zuzi-answer"><span class="cb-fuxi-zuzi-answer-label">${named}</span><span class="cb-fuxi-zuzi-sum" lang="${simplifiedLanguage}">${fuxiAssemblySum(terms)}</span></p>`;
}

/** Parts listed in a sentence, the last two joined by a word. */
function listed(
  parts: readonly string[],
  between: string,
  last: string,
): string {
  return parts.length < 2
    ? parts.join("")
    : `${parts.slice(0, -1).join(between)}${last}${parts.at(-1)!}`;
}

/**
 * The square, holding the character, which the script takes apart and puts
 * back together.
 *
 * @param assembly - The character's assembly.
 * @param outlines - Where the character's stroke outlines are published.
 * @returns The square.
 */
export function fuxiAssembledSquare(
  assembly: HanziAssembly,
  outlines: string,
): string {
  const whole = `<text class="cb-fuxi-zuzi-whole" x="50" y="88" font-size="100" text-anchor="middle" lang="${simplifiedLanguage}">${escapeHtml(assembly.character)}</text>`;
  const named = escapeHtml(
    bilingualText(
      `${assembly.character}, put together from ${listed(assembly.parts, ", ", " and ")}`,
      `${assembly.character}，由${listed(assembly.parts, "、", "和")}拼成`,
    ),
  );

  return `<div class="cb-fuxi-zuzi-square cb-fuxi-zuzi-done" role="img" aria-label="${named}" data-cb-fuxi-zuzi-done>
${fuxiAssemblyFrame({
  square: {
    boxes: assembly.boxes,
    parts: assembly.parts,
    strokes: assembly.strokes,
    outlines,
  },
  inside: whole,
})}
</div>`;
}
